import React, { useMemo, useRef, useState } from 'react';
import { useUi, useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, CdnMedia } from '@/components';
import { shuffleArray } from '@/utils/array';
import { Toolbar } from '../components/Toolbar';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { WhitePanel } from '../components/WhitePanel';
import { MultipleChoiceGame } from '../components/MultipleChoiceGame';

interface SpellingNounOrVerbStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
  nouns: Array<string>;
  verbs: Array<string>;
}

const SpellingNounOrVerbStep = ({
  activity,
  mode,
  wrapperClassName,
  index,
  total,
  word,
  nouns,
}: SpellingNounOrVerbStepProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  const [selectedChoices, setSelectedChoices] = useState<Array<string>>([]);

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      usePanel
      showTitle={false}
      showActivityPagination={false}
    >
      <Toolbar
        onPlay={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        hintPlay={!playedOnce}
        playDisabled={!playerReady}
      />

      <WhitePanel mode="basic" kind="fill" color="paper">
        <CdnMedia
          className="block mx-auto"
          symbol={word}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />

        <MultipleChoiceGame
          choices={['Noun', 'Verb']}
          correctChoice={nouns.includes(word) ? 'Noun' : 'Verb'}
          selectedChoices={selectedChoices}
          setSelectedChoices={setSelectedChoices}
          useGrid={false}
          squareChoices={false}
        />
      </WhitePanel>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </ActivityStep>
  );
};

export const SpellingNounOrVerb = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words || !activity.words2) {
    throw new Error('Words and words 2 are required for this activity');
  }

  const words = useMemo(
    () =>
      shuffleArray([
        ...(activity.words ? activity.words : []),
        ...(activity.words2 ? activity.words2 : []),
      ]),
    [activity.words, activity.words2],
  );

  return (
    <Activity
      steps={[
        ...(mode === 'TEACH'
          ? [
              <ActivityTitleStep
                key={`${activity.id}-title`}
                activity={activity}
                mode={mode}
                wrapperClassName={wrapperClassName}
                isPreview={isPreview}
                index={0}
                total={words.length + 1}
              />,
            ]
          : []),
        ...words?.flatMap((word, index) => [
          <SpellingNounOrVerbStep
            key={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={words.length + 1}
            word={word}
            nouns={activity.words || []}
            verbs={activity.words2 || []}
          />,
        ]),
      ]}
    />
  );
};
